import * as React from 'react'
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

type CommentDialogProps = {
	open: boolean
	onClose: () => void
	onSave: (comment: string) => Promise<void>
	initialComment: string
	activityLabel: string
	activityType: string
	canEdit: boolean
}

const CommentDialog = ({
	open,
	onClose,
	onSave,
	initialComment,
	activityLabel,
	activityType,
	canEdit,
}: CommentDialogProps) => {
	const [comment, setComment] = React.useState(initialComment)
	const [saving, setSaving] = React.useState(false)

	React.useEffect(() => {
		if (open) {
			setComment(initialComment)
		}
	}, [open, initialComment])

	const handleSave = async () => {
		setSaving(true)
		try {
			await onSave(comment.trim())
			onClose()
		} finally {
			setSaving(false)
		}
	}

	const contextLabel = [activityLabel, activityType].filter(Boolean).join(' · ')

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					pr: 1,
				}}
			>
				{canEdit ? 'Edit Comment' : 'Comment'}
				<IconButton aria-label='close' onClick={onClose} size='small' disabled={saving}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{contextLabel && (
					<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
						{contextLabel}
					</Typography>
				)}
				<TextField
					autoFocus={canEdit}
					multiline
					fullWidth
					minRows={5}
					maxRows={12}
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					placeholder='Enter your comment...'
					disabled={!canEdit || saving}
					InputProps={{ readOnly: !canEdit }}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
				<Button
					color='warning'
					variant='contained'
					onClick={onClose}
					disabled={saving}
					sx={{ minHeight: 44, minWidth: 100 }}
				>
					{canEdit ? 'Cancel' : 'Close'}
				</Button>
				{canEdit && (
					<Button
						color='success'
						variant='contained'
						onClick={handleSave}
						disabled={saving}
						sx={{ minHeight: 44, minWidth: 100 }}
					>
						{saving ? <CircularProgress size={22} color='inherit' /> : 'Save'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	)
}

export default CommentDialog
